import { useState, useEffect } from 'react';
import { api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { AlertTriangle, RefreshCw, X, Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ReturnDetailModal
 * Props:
 *   rmaNumber  – RTN-XXXX string to look up
 *   open       – boolean
 *   onClose    – () => void
 *   onVoided   – optional () => void  (called after successful void)
 */
export default function ReturnDetailModal({ rmaNumber, open, onClose, onVoided }) {
  const [ret, setRet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [managerPin, setManagerPin] = useState('');
  const [voiding, setVoiding] = useState(false);

  useEffect(() => {
    if (!rmaNumber || !open) return;
    setLoading(true);
    setRet(null);
    setVoidOpen(false);
    setVoidReason('');
    setManagerPin('');
    api.get(`${BACKEND_URL}/api/returns/by-rma/${encodeURIComponent(rmaNumber)}`)
      .then(r => setRet(r.data))
      .catch(e => {
        // 404 = orphaned partial-write return (inventory moved but doc never saved)
        // Don't toast — we show a contextual message in the modal body instead
        if (e?.response?.status !== 404) {
          toast.error(`Failed to load return ${rmaNumber}`);
        }
      })
      .finally(() => setLoading(false));
  }, [rmaNumber, open]);

  const handleVoid = async () => {
    if (!voidReason.trim()) { toast.error('Please enter a reason'); return; }
    if (!managerPin.trim()) { toast.error('Manager PIN required'); return; }
    setVoiding(true);
    try {
      await api.post(`${BACKEND_URL}/api/returns/${ret.id}/void`, {
        manager_pin: managerPin,
        reason: voidReason,
      });
      toast.success(`${ret.rma_number} voided — all effects reversed`);
      setVoidOpen(false);
      onVoided?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to void return');
    }
    setVoiding(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-mono text-base">
            {rmaNumber}
            {ret?.voided
              ? <Badge className="bg-red-100 text-red-700 border-red-200 text-xs font-sans">Voided</Badge>
              : ret?.status === 'processing'
                ? <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs font-sans">Incomplete</Badge>
                : ret
                  ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs font-sans">Active</Badge>
                  : null}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <RefreshCw size={14} className="animate-spin" /> Loading...
          </div>
        )}

        {!loading && !ret && !loading && (
          <div className="py-6 text-sm flex flex-col items-center gap-3">
            <Package size={28} className="text-amber-400" />
            <p className="font-semibold text-slate-700">Receipt not available</p>
            <p className="text-slate-500 text-center max-w-xs leading-relaxed">
              <span className="font-mono text-xs text-slate-600">{rmaNumber}</span> was
              processed and inventory was updated, but the receipt document was not saved
              (partial-write error). The inventory impact is valid — check Movement History
              for the full details. Contact your admin if a manual correction is needed.
            </p>
          </div>
        )}

        {ret && (
          <div className="space-y-4 mt-1">
            {/* Incomplete return warning */}
            {ret.status === 'processing' && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-500" />
                <span>This return is <strong>incomplete</strong> — inventory was updated but the refund/AR step did not finish. Wallet amounts below may be ₱0. Contact admin to review cashier balance.</span>
              </div>
            )}
            {/* Meta */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
              <div className="text-slate-500">Date</div>
              <div className="font-medium">{ret.return_date}</div>
              <div className="text-slate-500">Customer</div>
              <div className="font-medium">{ret.customer_name || 'Walk-in'}</div>
              <div className="text-slate-500">Reason</div>
              <div className="font-medium">{ret.reason}</div>
              {ret.invoice_number && <>
                <div className="text-slate-500">Invoice</div>
                <div className="font-medium font-mono text-xs">{ret.invoice_number}</div>
              </>}
              <div className="text-slate-500">Processed by</div>
              <div className="font-medium">{ret.processed_by_name || '—'}</div>
            </div>

            <Separator />

            {/* Items */}
            <div className="space-y-1.5">
              {(ret.items || []).map((it, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <span className="font-medium truncate block">{it.product_name}</span>
                    <span className="text-xs text-slate-400">× {it.quantity} {it.unit}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <Badge className={`text-[9px] ${it.inventory_action === 'shelf' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
                      {it.inventory_action === 'shelf' ? 'Back to shelf' : 'Pull-out'}
                    </Badge>
                    <span className="font-semibold text-[#1A4D2E] tabular-nums">
                      {formatPHP(it.refund_price * it.quantity)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <Separator />

            {/* Refund total + void info */}
            <div className="flex items-center justify-between text-sm">
              <div>
                <span className="text-slate-500">Refund: </span>
                <span className="font-bold text-slate-800">{formatPHP(ret.refund_amount)}</span>
                <span className="text-slate-400 ml-1 text-xs">via {ret.fund_source}</span>
              </div>
              {ret.notes && <span className="text-xs text-slate-400 italic truncate ml-3">{ret.notes}</span>}
            </div>

            {ret.voided && (
              <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                Voided by <strong>{ret.voided_by}</strong>
                {ret.void_reason && <> — {ret.void_reason}</>}
                {ret.void_authorized_by && ret.void_authorized_by !== ret.voided_by && (
                  <> (authorized by {ret.void_authorized_by})</>
                )}
              </div>
            )}

            {/* Void section */}
            {!ret.voided && (
              <div className="pt-1">
                {!voidOpen ? (
                  <Button
                    variant="outline"
                    className="w-full border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => setVoidOpen(true)}
                    data-testid="void-return-open-btn"
                  >
                    <X size={14} className="mr-1.5" /> Cancel / Void This Return
                  </Button>
                ) : (
                  <div className="space-y-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs font-semibold text-red-700 flex items-center gap-1.5">
                      <AlertTriangle size={13} />
                      This will fully reverse all effects — inventory, cash, AR, expenses.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-slate-600">Reason *</Label>
                        <Input
                          className="mt-1 h-8 text-sm"
                          value={voidReason}
                          onChange={e => setVoidReason(e.target.value)}
                          placeholder="e.g. Duplicate entry"
                          autoFocus
                          data-testid="void-reason-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Manager PIN *</Label>
                        <Input
                          className="mt-1 h-8 text-sm"
                          type="password"
                          value={managerPin}
                          onChange={e => setManagerPin(e.target.value)}
                          placeholder="••••"
                          onKeyDown={e => e.key === 'Enter' && handleVoid()}
                          data-testid="void-manager-pin-input"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline" size="sm"
                        className="flex-1"
                        onClick={() => { setVoidOpen(false); setManagerPin(''); setVoidReason(''); }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        onClick={handleVoid}
                        disabled={voiding || !voidReason.trim() || !managerPin.trim()}
                        data-testid="confirm-void-return-btn"
                      >
                        {voiding && <RefreshCw size={12} className="animate-spin mr-1" />}
                        Confirm Void
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
